import { CommonModule } from "@angular/common";
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	Inject,
	OnDestroy,
	OnInit,
} from "@angular/core";
import { FormControl, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatIconModule } from "@angular/material/icon";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { Subject, finalize, takeUntil } from "rxjs";

import { ContactService } from "src/app/modules/contacts/services/contact.service";
import { ContactDetailDto } from "src/app/modules/contacts/interfaces/contact.interface";
import {
	SolicitudCumplimentacionService,
} from "src/app/_core/providers/solicitud-cumplimentacion.service";
import { NotificationService } from "src/app/_core/providers/notification.service";
import { DialogService } from "src/app/_core/services/dialog.service";
import { EmpleadoAvatarComponent } from "src/app/modules/form-controls/components/empleado-avatar/empleado-avatar.component";
import {
	EmpleadoLike,
	empleadoTitleCase,
} from "src/app/_core/utils/empleado-display";

/** Datos de apertura: la entidad (Contrato/Centro) desde cuyo detalle se solicita. */
export interface SolicitudDatosDialogData {
	entityType: "Contrato" | "Centro";
	entityId: number;
	entityCodigo?: string;
	entityNombre?: string;
}

/** Longitud máxima del mensaje (alineada con el backend). */
const MENSAJE_MAX = 2000;

/**
 * Diálogo "Solicitar datos" (solicitud de cumplimentación): elige un destinatario
 * entre las personas de la entidad (preseleccionando a su responsable), redacta un
 * mensaje y envía la solicitud por correo (con copia al solicitante y enlace a la
 * ficha). Standalone para abrirse vía MatDialog desde contracts y main sin acoplar
 * módulos (mismo criterio que EmptyStateComponent).
 */
@Component({
	selector: "app-solicitud-datos-dialog",
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		MatIconModule,
		TranslateModule,
		EmpleadoAvatarComponent,
	],
	templateUrl: "./solicitud-datos-dialog.component.html",
	styleUrls: ["./solicitud-datos-dialog.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SolicitudDatosDialogComponent implements OnInit, OnDestroy {
	private readonly destroy$ = new Subject<void>();

	readonly mensajeMax = MENSAJE_MAX;
	readonly mensajeCtrl = new FormControl<string>("", {
		nonNullable: true,
		validators: [Validators.required, Validators.maxLength(MENSAJE_MAX)],
	});

	/** Personas de la entidad CON email (sin email no se puede enviar). */
	candidatos: ContactDetailDto[] = [];
	/** Destinatario actual (por defecto, el responsable de la entidad). */
	seleccionado: ContactDetailDto | null = null;
	/** Lista inline de "Cambiar destinatario" desplegada. */
	listaAbierta = false;

	isLoading = false;
	hasError = false;
	isSending = false;

	constructor(
		private readonly contactService: ContactService,
		private readonly solicitudService: SolicitudCumplimentacionService,
		private readonly notificationService: NotificationService,
		private readonly dialogService: DialogService,
		private readonly translate: TranslateService,
		private readonly cdr: ChangeDetectorRef,
		private readonly dialogRef: MatDialogRef<SolicitudDatosDialogComponent, boolean>,
		@Inject(MAT_DIALOG_DATA) public readonly data: SolicitudDatosDialogData,
	) {}

	ngOnInit(): void {
		this.loadPersonas();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	/** Resto de candidatos (la lista de "Cambiar destinatario"). */
	get otrosCandidatos(): ContactDetailDto[] {
		return this.candidatos.filter((c) => c !== this.seleccionado);
	}

	/** Sin personas con email no hay a quién enviar: aviso + envío deshabilitado. */
	get sinPersonas(): boolean {
		return !this.isLoading && !this.hasError && this.candidatos.length === 0;
	}

	get canSend(): boolean {
		return (
			!this.isSending &&
			!!this.seleccionado &&
			this.mensajeCtrl.valid &&
			!!this.mensajeCtrl.value.trim()
		);
	}

	/** Adaptador para `app-empleado-avatar` (ContactDetailDto → EmpleadoLike). */
	empleadoLikeOf(c: ContactDetailDto): EmpleadoLike {
		return {
			id: c.empleadoId,
			nombre: c.empleadoNombre,
			email: c.empleadoEmail,
		};
	}

	/** Nombre "bonito" (mismo casing que el resto de la app). */
	nombreDe(c: ContactDetailDto | null): string {
		return empleadoTitleCase(c?.empleadoNombre || "");
	}

	toggleLista(): void {
		this.listaAbierta = !this.listaAbierta;
		this.cdr.markForCheck();
	}

	seleccionar(c: ContactDetailDto): void {
		this.seleccionado = c;
		this.listaAbierta = false;
		this.cdr.markForCheck();
	}

	close(): void {
		this.dialogRef.close(false);
	}

	/** Confirmación previa al POST (patrón DialogService.confirm del repo). */
	onEnviar(): void {
		if (!this.canSend || !this.seleccionado) {
			return;
		}
		this.dialogService
			.confirm({
				type: "confirm",
				icon: "outgoing_mail",
				title: this.translate.instant("solicitudDatos.confirmTitle"),
				message: this.translate.instant("solicitudDatos.confirmMsg", {
					name: this.nombreDe(this.seleccionado),
				}),
				confirmText: this.translate.instant("solicitudDatos.confirmCta"),
				cancelText: this.translate.instant("common.cancel"),
			})
			.pipe(takeUntil(this.destroy$))
			.subscribe((confirmed) => {
				if (confirmed) {
					this.enviar();
				}
			});
	}

	// ---- Carga de personas de la entidad ----

	private loadPersonas(): void {
		this.isLoading = true;
		this.hasError = false;
		this.cdr.markForCheck();

		this.contactService
			.getContactsByEntityId(this.data.entityId, this.data.entityType)
			.pipe(
				takeUntil(this.destroy$),
				finalize(() => {
					this.isLoading = false;
					this.cdr.markForCheck();
				}),
			)
			.subscribe({
				next: (contacts) => {
					this.candidatos = this.dedupe(
						(contacts || []).filter((c) =>
							(c.empleadoEmail || "").trim(),
						),
					);
					// Preselección: el responsable (primer registro esResponsable=true);
					// si no lo hay (o no tiene email), la primera persona con email.
					this.seleccionado =
						this.candidatos.find((c) => c.esResponsable) ??
						this.candidatos[0] ??
						null;
				},
				error: () => {
					this.hasError = true;
					this.candidatos = [];
					this.seleccionado = null;
				},
			});
	}

	/** Una entrada por empleado (puede figurar como responsable Y como contacto). */
	private dedupe(contacts: ContactDetailDto[]): ContactDetailDto[] {
		const byEmpleado = new Map<number, ContactDetailDto>();
		for (const c of contacts) {
			const prev = byEmpleado.get(c.empleadoId);
			// El registro de responsable prevalece (muestra el badge correcto).
			if (!prev || (c.esResponsable && !prev.esResponsable)) {
				byEmpleado.set(c.empleadoId, c);
			}
		}
		return Array.from(byEmpleado.values());
	}

	// ---- Envío ----

	private enviar(): void {
		const dest = this.seleccionado;
		if (!dest) {
			return;
		}
		this.isSending = true;
		this.cdr.markForCheck();

		this.solicitudService
			.crear({
				entidad: this.data.entityType,
				entidadId: this.data.entityId,
				destinatarioEmpleadoId: dest.empleadoId,
				destinatarioEmail: (dest.empleadoEmail || "").trim(),
				destinatarioNombre: dest.empleadoNombre || "",
				mensaje: this.mensajeCtrl.value.trim(),
			})
			.pipe(
				takeUntil(this.destroy$),
				finalize(() => {
					this.isSending = false;
					this.cdr.markForCheck();
				}),
			)
			.subscribe({
				next: (res) => {
					if (res?.emailEnviado === false) {
						this.notificationService.showWarning(
							this.translate.instant("solicitudDatos.successNoEmail"),
						);
					} else {
						this.notificationService.showSuccess(
							this.translate.instant("solicitudDatos.successToast"),
							this.translate.instant("common.success"),
						);
					}
					this.dialogRef.close(true);
				},
				error: () => {
					this.notificationService.showError(
						this.translate.instant("solicitudDatos.errorToast"),
					);
				},
			});
	}
}
